import { useCallback, useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';

import { Text } from '@/components/Text';
import { Toggle } from '@/components/ui/therapist/toggle';
import { Card } from '@/components/ui/therapist/Card';
import { DeleteAccountModal } from '@/components/DeleteAccountModal';
import {
  deleteUser,
  fetchUserConsents,
  localLogoutUser,
} from '@/api/services/user';
import { useStoreState } from '@/stores/store';
import { GlobalStateModel } from '@/stores/state/globalState';
import { updateConsent } from '@/utils/consents';
import { logError } from '@/utils/logHandling';
import { BodyWrapper } from '@/layouts/BodyWrapper';

import { ExpandableSection } from './ExpandableSection';
import { styles } from './styles';

const consentToTitle: { [key: string]: string } = {
  PaymentProcessing: 'Zahlungsabwicklung durch Stripe',
  StudyConsent: 'Analysedaten sammeln',
  DataUsageConsent: 'Analysedaten sammeln',
  DataProcessing: 'Verarbeitung personenbezogener Daten',
};

export function Consent() {
  const userConsents = useStoreState((state) => state.userConsents);

  const [isAccountDeletionModalVisible, setIsAccountDeletionModalVisible] =
    useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [value, setValue] = useState('');

  const toggleExpanded = useCallback((consentId: string) => {
    setExpandedItems((prev) =>
      prev.includes(consentId)
        ? prev.filter((id) => id !== consentId)
        : [...prev, consentId],
    );
  }, []);

  const cancelAccountDeletion = () => {
    setIsAccountDeletionModalVisible(false);
    setValue('');
  };

  const confirmAccountDeletion = async () => {
    cancelAccountDeletion();

    try {
      await deleteUser();
      await localLogoutUser(true);
    } catch (error) {
      logError('Could not delete user', error);
      await localLogoutUser();
    }
  };

  async function handleConsentChange(
    consent: GlobalStateModel['userConsents'][0],
  ) {
    try {
      await updateConsent(consent._id, !consent.givenConsent);
      await fetchUserConsents();
    } catch (error) {
      logError('Could not update user consent', error);
    }
  }

  useEffect(() => {
    fetchUserConsents();
  }, []);

  return (
    <BodyWrapper>
      <Card>
        <Text>
          In diesem Bereich können Sie Ihre Einwilligungen für verschiedene
          Funktionen der App verwalten – erteilen Sie neue Zustimmungen oder
          widerrufen Sie bestehende ganz einfach. Einige Einwilligungen sind für
          die Nutzung der App erforderlich.
        </Text>
      </Card>

      <Card styleBody={{ padding: 0 }}>
        {userConsents.map((consent, index) => (
          <View key={consent._id}>
            <View style={styles.consentContainer}>
              <Text style={styles.consentTypeLabel}>
                {consent.revocable
                  ? 'optional'
                  : 'erforderlich für Nutzung der App'}
              </Text>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Text style={styles.consentTitle}>
                  {consentToTitle[consent.name]}
                </Text>
                {consent.revocable ? (
                  <Toggle
                    value={consent.givenConsent}
                    onToggle={() => handleConsentChange(consent)}
                  />
                ) : (
                  <Pressable
                    onPress={() =>
                      consent.givenConsent
                        ? setIsAccountDeletionModalVisible(true)
                        : null
                    }
                  >
                    <Text style={styles.unrevokableConsentLabel}>
                      {consent.givenConsent ? 'Widerrufen' : 'Zustimmen'}
                    </Text>
                  </Pressable>
                )}
              </View>

              <ExpandableSection
                isExpanded={expandedItems.includes(consent._id)}
                onToggle={() => toggleExpanded(consent._id)}
                content={consent.consentText}
              />
            </View>
            {index !== userConsents.length - 1 && (
              <View style={styles.divider}></View>
            )}
          </View>
        ))}
      </Card>

      <DeleteAccountModal
        isVisible={isAccountDeletionModalVisible}
        onClose={cancelAccountDeletion}
        onConfirm={confirmAccountDeletion}
        value={value}
        onChangeValue={(text) => setValue(text)}
      />
    </BodyWrapper>
  );
}
